import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import moment from 'moment';
// import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import CollapsibleGrah from 'components/CollapsibleGraph';
import CONSTANTS from 'utils/constants';

// import paths from 'pages/Router/paths';
import { entitiesCleanUp } from 'state/actions/stations';
import {
  fetchInstallationEntities,
  startDarkening,
  stopDarkening,
  resetDarkening,
  processDarkeningSNR,
} from 'state/actions/stationsData';
import { useFormatMessage } from 'hooks';
import {
  parseEntityDataToComponents,
  getFormFieldsComponents,
  getCardComponent,
  getFormatMessage,
} from 'utils/schema';
import paths from 'pages/Router/paths';

import { getHexSerialNumber } from 'utils/hdrainHelper';
import {
  infoSchemas,
  contactSchemas,
  // versionSchemas,
  satelliteSchemas,
  // phoneSchemas,
  // measuresSchemas,
} from '../schema';

import './index.scss';

const computeGraphTicks = (min, max, step) => {
  const ticks = [];
  for (let i = min; i <= max; i += step) {
    ticks.push(i);
  }
  return ticks;
};

const StationInstallation = ({ entity, onSubmitHandler, schema, options }) => {
  // const i18nScreenName = 'StationInstallation';
  const i18nEntityName = 'Station';
  const entitiesName = 'stations';
  const PATH_ENTITIES = paths.STATIONS;

  // const entityWithStatus = entity;
  const { serialNumber } = entity;

  const [entityWithStatus, setEntityWithStatus] = useState(entity);
  const [nowMoment, setNowMoment] = useState(moment());
  const [stationInstallationData, setStationInstallationData] = useState([]);
  const [yGraphDomain, setYGraphDomain] = useState([-36, -14]);
  const defaultYGraphTicks = computeGraphTicks(
    yGraphDomain[0],
    yGraphDomain[1],
    2
  );
  const [yGraphTicks, setYGraphTicks] = useState(defaultYGraphTicks);

  const [xGraphDomain, setXGraphDomain] = useState(['auto', 'auto']);
  const [darkeningCounter, setDarkeningCounter] = useState(null);
  const [
    darkeningProcessResultMessage,
    setDarkeningProcessResultMessage,
  ] = useState(null);
  const [
    darkeningProcessResultClass,
    setDarkeningProcessResultClass,
  ] = useState('is-info');

  const {
    loading,
    success,
    installationData,
    isDarkening,
    darkeningStartedAt,
    darkeningStoppedAt,
    darkeningSNR,
    isProcessingDarkeningSNR,
    isProcessingDarkeningSNRSuccess,
  } = useSelector(
    (state) => ({
      loading: state[entitiesName].loading,
      success: state[entitiesName].success,
      installationData: state.stationsData.installationData,
      isDarkening: state.stationsData.isDarkening,
      darkeningStartedAt: state.stationsData.darkeningStartedAt,
      darkeningStoppedAt: state.stationsData.darkeningStoppedAt,
      darkeningSNR: state.stationsData.darkeningSNR,
      isProcessingDarkeningSNR: state.stationsData.isProcessingDarkeningSNR,
      isProcessingDarkeningSNRSuccess:
        state.stationsData.isProcessingDarkeningSNRSuccess,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();
  const { register, watch, handleSubmit, errors, setValue } = useForm({
    defaultValues: { ...schema.default(), ...entity },
    resolver: yupResolver(schema),
  });
  const watchAllFields = watch();
  const { darkeningSignal } = watchAllFields;

  // Run an interval to fetch installation data every 15s
  useEffect(() => {
    dispatch(fetchInstallationEntities(serialNumber));

    const intervalId = setInterval(() => {
      dispatch(fetchInstallationEntities(serialNumber));
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Run an interval to update now date
  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = moment();
      setNowMoment(now);
    }, 500);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Get and sort installation data
  useEffect(() => {
    const hexSerialNumber = getHexSerialNumber(serialNumber);
    const currentStationInstallationData =
      installationData &&
      installationData.signal &&
      installationData.signal[hexSerialNumber];
    if (currentStationInstallationData) {
      const lastHourMoment = moment().subtract(30, 'minute');
      const lastHourData = currentStationInstallationData
        .filter((sample) => {
          const { epoch } = sample;
          if (moment(epoch).isAfter(lastHourMoment)) {
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          const epochA = a.epoch;
          const epochB = b.epoch;
          if (epochA < epochB) {
            return 1;
          }
          return -1;
        });
      setStationInstallationData(lastHourData);
    }
  }, [installationData]);

  // Update darkening status when process is stopped
  useEffect(() => {
    if (!isDarkening && darkeningStartedAt && darkeningStoppedAt) {
      entityWithStatus.darkeningStartedAt = darkeningStartedAt;
      entityWithStatus.darkeningStoppedAt = darkeningStoppedAt;
      setEntityWithStatus(entityWithStatus);
      setValue('darkeningStartedAt', darkeningStartedAt);
      setValue('darkeningStoppedAt', darkeningStoppedAt);
      dispatch(processDarkeningSNR(serialNumber, darkeningSignal));
    }
  }, [isDarkening]);

  // Update darkening counter
  useEffect(() => {
    let intervalId = null;
    if (isDarkening && darkeningStartedAt) {
      intervalId = setInterval(() => {
        const now = moment();
        const darkeningStartedAtPlus30s = moment(darkeningStartedAt).add(
          30,
          's'
        );
        const diffMinutes = now.diff(darkeningStartedAtPlus30s, 'minutes');
        const diffMinutesStr =
          diffMinutes > 10 ? diffMinutes : `0${diffMinutes}`;
        const diffSeconds = now.diff(darkeningStartedAtPlus30s, 'seconds') % 60;
        const diffSecondsStr =
          diffSeconds > 10 ? diffSeconds : `0${diffSeconds}`;

        const darkeningCounterStr = `${diffMinutesStr}min${diffSecondsStr}s`;
        setDarkeningCounter(darkeningCounterStr);
      }, 500);
    } else {
      setDarkeningCounter(null);
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isDarkening]);

  // Handle Darkening process result
  useEffect(() => {
    const { darkeningSNRThreshold } = watchAllFields;
    let message = null;
    let className = 'is-info';
    if (isProcessingDarkeningSNRSuccess) {
      if (darkeningSNR > parseFloat(darkeningSNRThreshold) + 0.5) {
        message = `Good. Current value (${darkeningSNR} dB) is  > ${darkeningSNRThreshold} dB.`;
        className = 'is-success';
        setValue('darkeningSNR', darkeningSNR);
      } else if (darkeningSNR > parseFloat(darkeningSNRThreshold)) {
        message = `Just enough. Current value (${darkeningSNR} dB) is ~ ${darkeningSNRThreshold} dB. Please retry.`;
        className = 'is-warning';

        setValue('darkeningSNR', darkeningSNR);
      } else {
        message = `Insufficient. Current value (${
          darkeningSNR === null ? 'N/A' : darkeningSNR
        } dB) is not > ${darkeningSNRThreshold} dB. Please retry.`;
        className = 'is-danger';
      }
      setDarkeningProcessResultMessage(message);
      setDarkeningProcessResultClass(className);
    }
  }, [isProcessingDarkeningSNR, isProcessingDarkeningSNRSuccess, darkeningSNR]);

  useEffect(() => {
    return () => dispatch(resetDarkening());
  }, []);

  useEffect(() => {
    return () => dispatch(entitiesCleanUp());
  }, [dispatch, success]);

  const [dataComponents, setDataComponents] = useState({});
  const [cards, setCards] = useState([]);

  // Generate info fields and form fields
  useEffect(() => {
    const { satellites } = options;
    const entityOptions = { i18nEntityName, satellites };
    const infoDataComponent = parseEntityDataToComponents(
      entityWithStatus,
      infoSchemas,
      entityOptions
    );
    const contactDataComponent = parseEntityDataToComponents(
      entityWithStatus,
      contactSchemas,
      entityOptions
    );

    const _cards = [
      {
        header: {
          title: getFormatMessage(entityWithStatus, 'satellite', {
            i18nEntityName,
          }),
          icon: 'mdi mdi-satellite-variant',
        },
        fields: getFormFieldsComponents(
          satelliteSchemas,
          errors,
          register,
          'edit',
          entityWithStatus,
          entityOptions
        ),
      },
    ];
    dataComponents.infoDataComponent = infoDataComponent;
    dataComponents.contactDataComponent = contactDataComponent;

    setDataComponents(dataComponents);
    setCards(_cards);
  }, [entityWithStatus, options]);

  // Update graph domains as inputs change
  const graphInputChange = (evt) => {
    const target = evt.currentTarget;
    const { name } = target;
    const { value } = target;

    if (name === 'graphYAxisMinimum' || name === 'graphYAxisMaximum') {
      const currentYGraphDomain = yGraphDomain;
      if (name === 'graphYAxisMinimum') {
        currentYGraphDomain[0] = parseInt(value, 10);
      } else if (name === 'graphYAxisMaximum') {
        currentYGraphDomain[1] = parseInt(value, 10);
      }

      // Process ticks
      const currentYGraphTicks = computeGraphTicks(
        currentYGraphDomain[0],
        currentYGraphDomain[1],
        2
      );
      setYGraphDomain(currentYGraphDomain);
      setYGraphTicks(currentYGraphTicks);
    } else if (name === 'graphXAxisMinimum' || name === 'graphXAxisMaximum') {
      const currentXGraphDomain = xGraphDomain;
      const splittedValue = value.split(':');
      const hours = parseInt(splittedValue[0], 10);
      const minutes = parseInt(splittedValue[1], 10);
      let valueInMs = 'auto';
      if (hours && minutes) {
        valueInMs = moment.utc().hours(hours).minutes(minutes).valueOf();
      }
      if (name === 'graphXAxisMinimum') {
        currentXGraphDomain[0] = valueInMs;
      } else if (name === 'graphXAxisMaximum') {
        currentXGraphDomain[1] = valueInMs;
      }
      setXGraphDomain(currentXGraphDomain);
    }
  };

  const handleStartDarkening = () => {
    const now = moment();
    // Subtract 30s to get the first value
    const previousTimeStep = now.subtract(30, 'seconds');
    previousTimeStep.milliseconds(0);
    dispatch(
      startDarkening({ darkeningStartedAt: previousTimeStep.toISOString() })
    );
  };

  const handleStopDarkening = () => {
    dispatch(stopDarkening());
  };

  const mitigationsLines = CONSTANTS.SIGNALS.map((signal, i) => {
    return { dataKey: signal, stroke: CONSTANTS.COLORS[i] };
  });

  const goBackMessage = useFormatMessage(`${i18nEntityName}.goBack`);
  return (
    <>
      <div className="block station-profile-block">
        <header className="card-header">
          <h4 className="title is-4">
            <span className="icon">
              <i className="mdi mdi-information-outline default" />
            </span>
            &nbsp;{useFormatMessage(`${i18nEntityName}.info`)}
          </h4>
        </header>
        <div className="card-content">{dataComponents.infoDataComponent}</div>
      </div>

      <div className="block station-profile-block">
        <header className="card-header">
          <h4 className="title is-4">
            <span className="icon">
              <i className="mdi mdi-account-box default" />
            </span>
            &nbsp;{useFormatMessage(`${i18nEntityName}.contact`)}
          </h4>
        </header>
        <div className="card-content">
          {dataComponents.contactDataComponent}
        </div>
      </div>

      <div className="card tile is-child">
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          {cards.map(getCardComponent)}

          <div>
            <ul>
              <li>
                Running task: {isDarkening ? 'darkening...' : ''}
                {isProcessingDarkeningSNR ? 'processing SNR...' : ''}
              </li>
              {!isProcessingDarkeningSNR &&
                (isDarkening ? (
                  <button
                    type="button"
                    className="button is-info is-fullwidth"
                    onClick={handleStopDarkening}
                  >
                    Stop darkening
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button is-info is-fullwidth"
                    onClick={handleStartDarkening}
                  >
                    Start darkening
                  </button>
                ))}
              {(isDarkening || isProcessingDarkeningSNR) && (
                <progress className="progress is-info my-4" max="100" />
              )}
              {isDarkening && (
                <li>
                  Darkening Counter:{' '}
                  <span className="has-text-weight-semibold">
                    {darkeningCounter}
                  </span>
                </li>
              )}
              <li>Started at: {darkeningStartedAt}</li>
              <li>Stopped at: {darkeningStoppedAt}</li>
              {darkeningSNR !== null && (
                <li>darkeningSNR: {darkeningSNR} dB</li>
              )}
            </ul>
            {!isProcessingDarkeningSNR && darkeningProcessResultMessage && (
              <div className={`notification ${darkeningProcessResultClass}`}>
                {darkeningProcessResultMessage}
              </div>
            )}
          </div>

          <div className="my-4">
            <h5 className="title is-5">
              Current time: {nowMoment.utc().format('HH[h]mm:ss')} UTC
            </h5>
            <h3 className="title is-3 mb-0">Last data ({darkeningSignal})</h3>
            <div className="station-installation-raw-data-list has-background-light py-4">
              <ul>
                {stationInstallationData.map((sample) => {
                  const { epoch } = sample;
                  const value = sample[darkeningSignal];
                  const epochMoment = moment(epoch);
                  const epochUTC = epochMoment.utc().format('HH[h]mm:ss');
                  let darkeningClassNames = '';
                  // Highlight data during darkening
                  // NB: if in process, 'darkeningStoppedAt' is not defined.

                  if (isDarkening && epochMoment.isAfter(darkeningStartedAt)) {
                    darkeningClassNames = 'has-background-info has-text-light';
                  } else if (!isDarkening) {
                    if (
                      epochMoment.isAfter(
                        entityWithStatus.darkeningStartedAt
                      ) &&
                      epochMoment.isBefore(entityWithStatus.darkeningStoppedAt)
                    ) {
                      darkeningClassNames =
                        'has-background-info has-text-light';
                    }
                  }

                  return (
                    <li key={epoch} className={`px-2 ${darkeningClassNames}`}>
                      {epochUTC} UTC : {value} dB
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div>
            <h3 className="title is-3">Graph</h3>
            <div>
              <div className="field ">
                <label className="label my-4">Time axis</label>
                <div className="control columns is-mobile is-flex is-justify-content-space-around">
                  <input
                    type="time"
                    name="graphXAxisMinimum"
                    className="column is-5"
                    id=""
                    onChange={graphInputChange}
                  />
                  <input
                    type="time"
                    name="graphXAxisMaximum"
                    className="column is-5"
                    id=""
                    onChange={graphInputChange}
                  />
                </div>
              </div>
            </div>
            <div className="my-4">
              <div className="field">
                <label className="label my-4">Mitigations axis</label>
                {/* <div className="field-body "> */}
                <div className="control columns is-mobile is-flex is-justify-content-space-around">
                  <input
                    type="number"
                    name="graphYAxisMinimum"
                    className="column is-5"
                    id=""
                    step="2"
                    defaultValue={yGraphDomain[0]}
                    onChange={graphInputChange}
                  />
                  <input
                    type="number"
                    name="graphYAxisMaximum"
                    className="column is-5"
                    id=""
                    step="2"
                    defaultValue={yGraphDomain[1]}
                    onChange={graphInputChange}
                  />
                </div>
              </div>

              <div />
            </div>
            <CollapsibleGrah
              id="collapsible-mitigations"
              title="Mitigations"
              data={stationInstallationData}
              XAxisDataKey="epoch"
              Lines={mitigationsLines}
              domain={xGraphDomain}
              yGraphDomain={yGraphDomain}
              yTicks={yGraphTicks}
            />
          </div>

          <hr />
          <div className="field is-horizontal">
            <div className="field-label" />
            <div className="field-body">
              <div className="field">
                <div className="field is-grouped is-grouped-centered">
                  <div className="control">
                    <button
                      type="submit"
                      className={`button is-primary ${loading && 'is-loading'}`}
                    >
                      <span>
                        {useFormatMessage(`${i18nEntityName}.submit`)}
                      </span>
                    </button>
                  </div>
                  <div className="control">
                    <Link
                      to={`${PATH_ENTITIES}/${entity.id}/profile`}
                      className="button"
                    >
                      {goBackMessage}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

StationInstallation.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      postalCode: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
    }),
    createdAt: PropTypes.string,
  }).isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  schema: PropTypes.object.isRequired,
};

StationInstallation.defaultProps = {};

export default StationInstallation;
